// DividerPopover — Sprint machine-message-collapse (D7).
//
// The presentational "rule · pill-button · rule + click-to-open popover"
// row, extracted VERBATIM from `CompactionDivider.tsx` (sprint
// compaction-visibility D11 + compaction-divider-v2 D2/D3) the moment a
// second consumer appeared (`CollapsedMachineMessage`). Two copies of the
// same markup is the class P3/P6 say to kill now, not later.
//
// Contract the extraction must keep (AC7 of the sprint): the row is FLAT —
// `PopoverTrigger asChild` renders the `<button>` as a direct child of the
// row, so the trigger's `previousElementSibling` / `nextElementSibling`
// are the two `Separator`s (`CompactionDivider.test.tsx` asserts exactly
// that) and the compaction visual baseline stays byte-identical. Any
// wrapper element here breaks both. Test-ids are templated by prefix so
// `compaction-divider{,-rule,-trigger,-popover}` do not change.
//
// CLICK, not hover (D11 R1 change 7; founder re-confirmed 2026-09-12 for
// the machine-message use): popover bodies scroll, hover would dismiss on
// the way to them, and the trigger is a real `<button>` with
// `aria-expanded` so keyboard users reach it. `title` is the one-line hint.
//
// Zero design tokens introduced: every class below is the one the
// compaction trigger already carried (`design-discipline.md`).
//
// DS.8 (D20, taking DS.7 D19's handoff) — `./ui/popover` and `./ui/separator`
// are retired; this file imports `@radix-ui/react-popover` DIRECTLY and draws
// its own rules. The two shadcn files were 29-LOC `cn()` WRAPPERS, not the
// primitives, so retiring them never required adopting `@ds/overlays/Popover`
// — which is measured incompatible on two counts, both re-confirmed here at
// the pinned v0.2.0: (a) the DS piece renders its trigger inside TWO divs
// (`.wrapper`, `.trigger`), which breaks the FLAT-ROW contract above and the
// two `previousElementSibling`/`nextElementSibling` assertions in
// `CompactionDivider.test.tsx`; no adapter can flatten a piece's own DOM.
// (b) its panel is `position: absolute` with no portal and no collision
// detection, so it would be clipped by `ConversationList.tsx`'s
// `absolute inset-0 overflow-y-auto`. Today's Portal escape is kept verbatim.
// The fence above is PRESERVED, not crossed: `asChild` stays, the row stays
// flat, the five `testIdPrefix` expressions are untouched. Filed upstream as
// issue (a') instead of forked (DS-C4).
//
// `POPOVER_CONTENT_CLASSES` and the two rule `<div>`s carry `ui/popover.tsx`'s
// and `ui/separator.tsx`'s class blocks verbatim — except that the retired
// wrapper's arbitrary one-pixel height is written here as Tailwind's `h-px`
// (the same rendered hairline, a scale class instead of an arbitrary value).
// That is how the `ui/separator.tsx arb-px 2` Gate AA row drains BY
// RETIREMENT rather than being re-baselined under a new path.

import type { ReactNode } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import { Icon } from './atoms/Icon';
import type { IconName } from './atoms/Icon';
import { cn } from '../lib/utils';

/** `ui/popover.tsx`'s `PopoverContent` class block, verbatim (D20). */
const POPOVER_CONTENT_CLASSES =
  'z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';

/**
 * `ui/separator.tsx`'s horizontal class block, verbatim except that its
 * arbitrary one-pixel height becomes the `h-px` scale class (identical
 * hairline, no arbitrary value), plus the `flex-1` both call sites already
 * passed. `role="none"` + `data-orientation`
 * reproduce what Radix's `Separator` emitted at `decorative` (shadcn's default),
 * so the a11y tree is unchanged too.
 */
const RULE_CLASSES = 'shrink-0 bg-border h-px w-full flex-1';

export interface DividerPopoverProps {
  icon: IconName;
  /** Pill text, already translated. */
  label: string;
  /** One-line hover hint on the trigger, already translated. */
  title: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Popover body. Rendered only while open (Radix `Presence`), never CSS-hidden. */
  children: ReactNode;
  /** `<prefix>`, `<prefix>-rule`, `<prefix>-trigger`, `<prefix>-popover`. */
  testIdPrefix: string;
  /** Spread verbatim on the root row — `data-*` attributes only. */
  rootAttrs?: Record<`data-${string}`, string | boolean | undefined>;
}

export function DividerPopover({
  icon,
  label,
  title,
  open,
  onOpenChange,
  children,
  testIdPrefix,
  rootAttrs,
}: DividerPopoverProps) {
  return (
    <div className="flex items-center gap-3 py-2" data-testid={testIdPrefix} {...rootAttrs}>
      <div
        role="none"
        data-orientation="horizontal"
        className={RULE_CLASSES}
        data-testid={`${testIdPrefix}-rule`}
      />
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-expanded={open}
            title={title}
            data-testid={`${testIdPrefix}-trigger`}
            className="shrink-0 whitespace-nowrap text-caption px-2 py-1 rounded-full border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer"
          >
            <span className="inline-flex items-center gap-1">
              <Icon name={icon} size={12} className="shrink-0" aria-hidden />
              {label}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent
            side="top"
            align="center"
            sideOffset={4}
            className={cn(POPOVER_CONTENT_CLASSES, 'w-96')}
            data-testid={`${testIdPrefix}-popover`}
          >
            {children}
          </PopoverContent>
        </PopoverPortal>
      </Popover>
      <div
        role="none"
        data-orientation="horizontal"
        className={RULE_CLASSES}
        data-testid={`${testIdPrefix}-rule`}
      />
    </div>
  );
}
